<script>
export const pageName = 'certification-level-2';
export const pageRoute = '/certification/level-2';
export const title = 'Level 2 Certification'
export const requireMembership = true;
export const requireCompleteAccount = true;

import { getMembershipImportDate } from '../../../functions/src/site.config.js'
import CertFlightCardUrl from '../../resources/Cert-and-Record-flight-card-A4-v3-1.pdf'
import { listenToCurrentUserView } from '../../js/readModel.js';
import { PageShowError } from '@trullock/page-manager'

export default {
	data() {
		return {
			currentUser: null,
			CertFlightCardUrl,
			ready: false
		}
	},

	methods: {
		getMembershipImportDate,
		async boot(opts) {
			await listenToCurrentUserView(user => this.currentUser = user)
			this.ready = true;
		},
		show(opts)
		{
			if(this.currentUser.certification.levelNumber < 1)
				throw new PageShowError('/', 'You have not passed L1', {}, 'replace');
		},
		stageBadgeText(stage)
		{
			return stage.status == 'pending' ? 'Pending' : 
				stage.status == 'passed' ? 'Passed' : 
				'Not started'
		},
		stageBadgeClass(stage)
		{
			return stage.status == 'pending' ? 'badge-secondary' : 
				stage.status == 'passed' ? 'badge-success' : 
				'badge-light'
		},
		attemptBadgeText(stage)
		{
			return stage.result == null ? 'Pending' : 
				stage.result == 'passed' ? 'Passed' : 
				'Failed'
		},
		attemptBadgeClass(stage)
		{
			return stage.result == null ? 'badge-secondary' : 
				stage.result == 'passed' ? 'badge-success' : 
				'badge-danger'
		}
	}
}

</script>
<template>
	<div v-if="ready" class="container py-5">
		<div class="row">
			<div class="col col-12">
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Level 2 Certification</h4>
					</div>
					<div class="card-body">
						<p>Level 2 certification is required to fly rockets with a combined impulse between the 'J' and 'L' class (between 640Ns and 5120Ns).</p>
						<p>The member is required to perform two successful flights:</p>
						<ul>
							<li>The first (qualification) flight must be flown on an H-class or I-class motor.</li>
							<li>The second flight must be flown on a J-class motor on a different airframe or different configuration from the qualification flight.</li>
						</ul>
						<p>Detailed information about the <a href="https://www.ukra.org.uk/certification/high-power-certification/" target="_blank">Level 2 Certification Process <span class="fe fe-external-link"></span></a> can be found on the main UKRA website.</p>
						<p>Download the <a :href="CertFlightCardUrl" target="_blank">Flight Card <span class="fe fe-download"></span></a> for use in the field.</p>
					</div>
				</div>
			</div>
		</div>
		<div class="row">
			<div class="col col-12 col-lg-8">
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">{{ currentUser.certification.level2.stage1.status != 'passed' ? 'Qualification Flight 1' : 'Certification Flight' }}</h4>
					</div>
					<div class="card-body" v-if="currentUser.certification.level2.status == 'passed'">
						<p>Congratulations! You've passed your Level 2 certification.</p>
						<p>Level 3 is the next challenge. <a href="/certification/level-3">Lets get started!</a></p>
						<p>Why not order a certificate to hang in your rocket workshop?</p>
						<a href="/certification/buy-a-certificate/level2" class="btn btn-primary btn-block">Order a certificate <span class="fe fe-award"></span></a>
					</div>
					<div class="card-body" v-else-if="currentUser.certification.level2.attemptPending">
						<p class="mb-0">Please wait whilst UKRA validates your attempt. You should hear back soon.</p>
					</div>
					<div class="card-body" v-else>
						<p class="mb-0">Add a new rocket/flight in the <a href="/rockets">Flight Logs</a> tool to submit your next certification flight.</p>
					</div>
				</div>
			</div>
			<div class="col col-12 col-lg-4">
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">Progress</h4>
					</div>
					<div class="card-body">
						<div class="text-secondary small mb-4">Complete both flights for Level 2.</div>
						<div v-if="currentUser.certification.level2.grantedOn && currentUser.certification.level2.grantedOn < getMembershipImportDate()" class="alert alert-light small mb-4"><span class="fe fe-alert-triangle"></span> You achieved Level 2 by the previous certification process, so your progress will show as incomplete.</div>
						<div v-else-if="currentUser.certification.level2.grantedOn" class="alert alert-light small mb-4"><span class="fe fe-alert-triangle"></span> You were granted Level 2 outside of the normal process, so your certification progress may show as incomplete.</div>
						<div class="list-group list-group-flush my-n3">
							<div class="list-group-item">
								<div class="row">
									<div class="col small">
										Qualification Flight 1
									</div>
									<div class="col-auto">
										<div :class="`badge ${stageBadgeClass(currentUser.certification.level2.stage1)}`">
											{{ stageBadgeText(currentUser.certification.level2.stage1) }}
										</div>
									</div>
								</div>
							</div>
							<div class="list-group-item">
								<div class="row">
									<div class="col small">
										Certification Flight
									</div>
									<div class="col-auto">
										<div :class="`badge ${stageBadgeClass(currentUser.certification.level2.stage2)}`">
											{{ stageBadgeText(currentUser.certification.level2.stage2) }}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="card">
					<div class="card-header">
						<h4 class="card-header-title">History</h4>
					</div>
					<div class="card-body">
						<div class="list-group list-group-flush my-n3" v-if="currentUser.certification.level2.stage1.attempts.length || currentUser.certification.level2.stage2.attempts.length">
							<div class="list-group-item" v-for="attempt in currentUser.certification.level2.stage1.attempts.sort((a,b) => a.submittedOn - b.submittedOn)">
								<div class="row">
									<div class="col small">
										<h5>Qualification Flight 1</h5>
										<span class="small text-muted">{{ attempt.submittedOn.format('yyyy/MM/dd HH:mm') }}</span>
									</div>
									<div class="col-auto">
										<div :class="`badge ${attemptBadgeClass(attempt)}`">
											{{ attemptBadgeText(attempt) }}
										</div>
									</div>
								</div>
							</div>
							<div class="list-group-item" v-for="attempt in currentUser.certification.level2.stage2.attempts.sort((a,b) => a.submittedOn - b.submittedOn)">
								<div class="row">
									<div class="col small">
										<h5>Certification Flight</h5>
										<span class="small text-muted">{{ attempt.submittedOn.format('yyyy/MM/dd HH:mm') }}</span>
									</div>
									<div class="col-auto">
										<div :class="`badge ${attemptBadgeClass(attempt)}`">
											{{ attemptBadgeText(attempt) }}
										</div>
									</div>
								</div>
							</div>
						</div>
						<p class="small text-muted mb-0" v-else>You have not previously submitted any L2 certification attempts.</p>
					</div>
				</div>
			</div>
		</div>
    </div>
</template>
